<script setup lang="ts" generic="T = string | number | { name: string; [key: string]: any }">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
import { useField } from 'vee-validate'
import { computed, ref, useTemplateRef, watch } from 'vue'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import { translationFunctionDefault } from '@/utilities/translations'

import type { DropdownOption, FormSelectProps } from './inputTypes'

import AvatarInitials from '../avatar/AvatarInitials.vue'
import ButtonTertiary from '../buttons/ButtonTertiary.vue'
import TooltipApplier from '../overlays/TooltipApplier.vue'

const { searchable = false, ...props } = defineProps<FormSelectProps<T>>()

const emit = defineEmits<{
  blur: []
  change: [T]
  manualInput: []
}>()
const tfDefault = translationFunctionDefault()
const input = useTemplateRef('my-input')
const inputDirty = ref(false)
const openUpward = ref(false)

const checkDropdownDirection = (event: MouseEvent): void => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const button = event.currentTarget as HTMLElement
  const menu = button.parentElement
  if (!menu) return
  const rect = menu.getBoundingClientRect()
  openUpward.value = window.innerHeight - rect.bottom < 220
}

const isObjectWithName = (value: unknown): value is { name: string } => {
  return typeof value === 'object' && value !== null && 'name' in value
}

const {
  errorMessage: vErrorMessage,
  value: vValue,
  // we need this because eslint would remove <T> from useField
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
} = useField<T>(() => {
  return props.name ?? ''
})

const formModel = defineModel<T>('selected', {
  get: (value) => {
    return props.name ? vValue.value : value
  },
  set: (value) => {
    if (props.name) {
      vValue.value = value
    }

    return value
  },
})

const selectedOption = computed(() => {
  const selectedSection = props.options.find((section) => {
    return !!section.find((option) => {
      if (isObjectWithName(option.optionValue) && isObjectWithName(formModel.value)) {
        return option.optionValue.name === formModel.value.name
      }

      return option.optionValue === formModel.value
    })
  })

  const theSelectedOption = selectedSection?.find((option) => {
    if (isObjectWithName(option.optionValue) && isObjectWithName(formModel.value)) {
      return option.optionValue.name === formModel.value.name
    }

    return option.optionValue === formModel.value
  })

  return theSelectedOption
})

// we dont need reactivity here
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const searchTerm = ref<string | undefined>(selectedOption.value?.optionLabel)
const filteredOptions = computed(() => {
  return props.options
    .map((section) => {
      return section.filter((option: DropdownOption<T>) => {
        if (!!searchTerm.value && searchable && inputDirty.value) {
          const searchTermLower = searchTerm.value.toLowerCase()
          const optionLabel = option.optionLabel.toLowerCase()

          return optionLabel.includes(searchTermLower)
        }

        return true
      })
    })
    .filter((section) => {
      return section.length > 0
    })
})

const existsAtLeastOneIconInOptions = computed(() => {
  return (
    props.options.flat().some((option: DropdownOption<T>) => {
      return !!option.icon
    }) || props.showAvatars
  )
})

const onItemSelected = (val: T): void => {
  formModel.value = val
  emit('blur')
  emit('manualInput')
}

const onAfterEnter = (): void => {
  if (searchable) {
    input.value?.focus()
    input.value?.select()
  }
}
const onAfterLeave = (): void => {
  if (searchable) {
    inputDirty.value = false
    searchTerm.value = selectedOption.value?.optionLabel
  }
}

const onKeyDown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter') {
    return
  }
  inputDirty.value = true
  event.stopPropagation()
}

watch(
  () => {
    return vValue.value
  },
  (newValue) => {
    formModel.value = newValue
  },
)

watch(
  () => {
    return formModel.value
  },
  (newValue) => {
    if (newValue !== undefined) {
      emit('change', newValue)
    }
  },
)

watch(selectedOption, (newValue) => {
  if (newValue) {
    searchTerm.value = newValue.optionLabel
  } else {
    searchTerm.value = undefined
  }
})
</script>

<template>
  <Menu
    as="div"
    class="group relative w-full items-end rounded-lg border pb-3 pl-5 pr-3 pt-1.5"
    :class="[
      vErrorMessage ? 'border-radical_red-500' : 'border-black-200',
      props.disabled
        ? 'cursor-not-allowed bg-white text-black-400 opacity-60'
        : 'cursor-pointer bg-white text-black-900 focus-within:border-lochmara-300 hover:border-lochmara-600',
    ]"
    :data-testid="testId"
  >
    <label v-if="label" class="left-5 top-3 text-xs font-medium text-black-400" for="">
      {{ label }}
    </label>

    <MenuButton
      class="inline-flex w-full items-center gap-x-1.5 text-sm font-normal"
      :class="[onWhiteBg ? '' : 'shadow', small ? 'px-2.5 py-0' : '']"
      :data-testid="TEST_IDS_CREATE.singleSelect.trigger"
      :disabled="props.disabled ?? false"
      @click="checkDropdownDirection"
    >
      <div
        class="flex w-full flex-1 items-center justify-between gap-2.5"
        :class="props.isFilledByAi ? 'italic text-lochmara-600' : ''"
      >
        <component :is="icon" v-if="icon" class="mr-3 h-5 w-5"> </component>
        <AvatarInitials
          v-if="showAvatars && !!selectedOption && !selectedOption.isHideIcon"
          :image="selectedOption?.image"
          :name="selectedOption?.optionLabel || ''"
          size="small"
        />
        <input
          v-if="searchable"
          ref="my-input"
          v-model="searchTerm"
          class="h-5 min-w-0 flex-1 truncate border-none p-0 text-sm focus:border-none focus:outline-none focus:ring-0"
          :class="selectedOption?.isPlaceholderOption ? 'text-black-300' : ''"
          :data-testid="TEST_IDS_CREATE.singleSelect.input"
          :placeholder="placeholder ?? ''"
          type="text"
          @keydown="onKeyDown"
        />
        <span
          v-else
          :class="!selectedOption || selectedOption.isPlaceholderOption ? 'text-black-300' : ''"
        >
          {{ selectedOption?.optionLabel || placeholder }}
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          class="-ml-2.5 aspect-square shrink-0 text-current"
          :class="[small ? 'w-4' : 'w-5']"
        />
      </div>
    </MenuButton>
    <!-- Inline error indicator, aligned with the label area like TextInput -->
    <div class="absolute right-2 top-3">
      <TooltipApplier
        v-if="!!vErrorMessage"
        :data-testid="testId ? `${testId}-error` : undefined"
        is-red
        :label="vErrorMessage"
      >
        <ExclamationCircleIcon class="h-4 w-4 text-radical_red-500" />
      </TooltipApplier>
    </div>

    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
      @after-enter="onAfterEnter"
      @after-leave="onAfterLeave"
    >
      <MenuItems
        class="absolute left-0 right-0 z-20 max-h-48 divide-y divide-black-100 overflow-y-auto rounded-md bg-white py-2.5 shadow-lg focus:outline-none"
        :class="openUpward ? 'bottom-full mb-1 origin-bottom-right' : 'top-full origin-top-right'"
        :data-testid="TEST_IDS_CREATE.singleSelect.options"
      >
        <!-- TODO: use uuid here -->
        <div v-for="(section, index) in filteredOptions" :key="index" class="space-y-1.5 py-1.5">
          <template v-for="option in section" :key="option.value">
            <MenuItem v-slot="{ active }">
              <a
                class="group/item flex items-center px-4 py-2 text-sm"
                :class="[
                  option === selectedOption || active
                    ? 'bg-lochmara-50 text-lochmara-600'
                    : 'text-black-900',
                ]"
                @click="() => onItemSelected(option.optionValue)"
              >
                <div v-if="existsAtLeastOneIconInOptions" class="mr-3 h-5 w-5">
                  <component
                    :is="option.icon"
                    v-if="option.icon"
                    aria-hidden="true"
                    class="group/item-hover:text-current h-5 w-5 text-black-400"
                  ></component>
                  <AvatarInitials
                    v-if="showAvatars && !option.isHideIcon"
                    :image="option.image"
                    :name="option.optionLabel"
                    size="small"
                  />
                </div>
                {{ option.optionLabel }}
              </a>
            </MenuItem>
          </template>
        </div>
        <MenuItem v-if="filteredOptions.length === 0 && searchTerm !== ''">
          <div class="px-4 py-2 text-sm">
            <span>{{ tfDefault('common:noResults') }}</span>
          </div>
        </MenuItem>

        <MenuItem
          v-if="!!emptyStateAction && filteredOptions.length === 0 && searchTerm !== ''"
          key="menu-item-2"
        >
          <div class="px-4 py-2">
            <ButtonTertiary
              :label="tfDefault(emptyStateAction.labelTranslationKey, { entityName: searchTerm })"
              @click="emptyStateAction.action"
            >
              <component :is="emptyStateAction.icon" class="mr-3 h-6 w-6 text-current" />
            </ButtonTertiary>
          </div>
        </MenuItem>
      </MenuItems>
    </Transition>
  </Menu>
</template>
